import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import UpgradeScreen from '../components/UpgradeScreen';
import { hasFullAccess } from '../lib/platform';
import { hapticImpact, nativeShare } from '../lib/native';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Home,
  Users,
  Plus,
  Building2,
  MapPin,
  Maximize,
  Heart,
  Eye,
  Trash2,
  Pencil,
  Upload,
  Image as ImageIcon,
  Search,
  TrendingUp,
  Bot,
  ChevronLeft,
  ChevronRight,
  Bed,
  Car,
  Leaf,
  Key,
  Handshake,
  AlertCircle,
  CheckCircle,
  MessageCircle,
  User,
  Calendar,
  Zap,
  Clock,
  XCircle,
  Sparkles,
  Star,
  Shield,
  Briefcase,
  UserCheck,
  Store,
  Tag,
  RotateCcw,
  ExternalLink
} from 'lucide-react';

// Catégories et sous-catégories de biens immobiliers
const PROPERTY_CATEGORIES = {
  appartements: {
    label: 'Appartements',
    subcategories: [
      { value: 'locatif_longue_duree', label: 'Locatif longue durée' },
      { value: 'locatif_meuble_lmnp', label: 'Locatif meublé (LMNP)' },
      { value: 'colocation', label: 'Colocation' },
      { value: 'coliving', label: 'Coliving' },
      { value: 'courte_moyenne_duree', label: 'Courte / moyenne durée' }
    ]
  },
  immeubles: {
    label: 'Immeubles de rapport',
    subcategories: [
      { value: 'immeuble_loue', label: 'Immeuble loué' },
      { value: 'immeuble_a_decouper', label: 'Immeuble à découper (copro)' },
      { value: 'immeuble_a_renover', label: 'Immeuble à rénover' }
    ]
  },
  marchand_biens: {
    label: 'Marchand de biens',
    subcategories: [
      { value: 'renovation_revente', label: 'Rénovation / revente' },
      { value: 'decoupe', label: 'Découpe' },
      { value: 'terrains_pa_pc_dp', label: 'Terrains - PA - PC - DP' },
      { value: 'changement_usage', label: "Changement d'usage" },
      { value: 'commercial_activites', label: 'Commercial - activités - artisanale' }
    ]
  },
  locaux_commerciaux: {
    label: 'Locaux commerciaux',
    subcategories: [
      { value: 'murs_loues', label: 'Murs loués' },
      { value: 'murs_a_louer', label: 'Murs à louer / repositionner' },
      { value: 'mixte_commerce_habitation', label: 'Mixte (commerce + habitation)' }
    ]
  },
  terrains_operations: {
    label: 'Terrains & opérations',
    subcategories: [
      { value: 'terrain_a_batir', label: 'Terrain à bâtir' },
      { value: 'division_parcellaire', label: 'Division parcellaire' },
      { value: 'petite_promo_permis', label: 'Petite promo / permis' }
    ]
  },
  opportunites: {
    label: 'Opportunités du mois',
    subcategories: [
      { value: 'opportunite_du_mois', label: 'Opportunité du mois' }
    ]
  }
};

// Types de vendeur
const SELLER_TYPES = [
  { value: 'proprietaire', label: 'Propriétaire', icon: User, description: 'Je suis propriétaire du bien' },
  { value: 'agent_immobilier', label: 'Agent Immobilier', icon: Briefcase, description: 'Je suis mandaté pour vendre ce bien' },
  { value: 'marchand_biens', label: 'Marchand de biens', icon: Store, description: 'Je suis marchand de biens professionnel' }
];

const getCategoryLabel = (categoryKey) => PROPERTY_CATEGORIES[categoryKey]?.label || categoryKey;
const getSubcategoryLabel = (categoryKey, subcategoryValue) => {
  const category = PROPERTY_CATEGORIES[categoryKey];
  if (!category) return subcategoryValue;
  const sub = category.subcategories.find(s => s.value === subcategoryValue);
  return sub?.label || subcategoryValue;
};
const getSellerTypeLabel = (type) => SELLER_TYPES.find(s => s.value === type)?.label || 'Propriétaire';
const getSellerTypeIcon = (type) => {
  const seller = SELLER_TYPES.find(s => s.value === type);
  return seller?.icon || User;
};

const PropertiesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const authDocInputRef = useRef(null);
  
  const isClubMember = hasFullAccess(user?.subscription_tier);
  
  const [properties, setProperties] = useState([]);
  const [myProperties, setMyProperties] = useState([]);
  const [soldProperties, setSoldProperties] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [savedListings, setSavedListings] = useState([]);
  const [aiMatches, setAiMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    ville: '',
    category: '',
    transaction_type: '',
    price_max: ''
  });
  
  // New property form
  const [newProperty, setNewProperty] = useState({
    title: '',
    description: '',
    category: '',
    subcategory: '',
    property_type: 'appartement',
    transaction_type: 'vente',
    price: '',
    surface: '',
    nb_pieces: '',
    nb_chambres: '',
    code_postal: '',
    ville: '',
    adresse: '',
    dpe: '',
    loyer_estime: '',
    charges_copro: '',
    taxe_fonciere: '',
    travaux_estimes: '',
    parking: false,
    balcon: false,
    terrasse: false,
    jardin: false,
    cave: false,
    ascenseur: false,
    seller_type: 'proprietaire',
    is_public: true
  });
  const [propertyImages, setPropertyImages] = useState([]);
  const [authorizationDoc, setAuthorizationDoc] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  useEffect(() => {
    if (isClubMember) {
      fetchAllData();
    }
  }, [isClubMember]);

  // Show upgrade screen for non-club members
  if (!isClubMember) {
    return (
      <UpgradeScreen
        icon={Building2}
        iconBgClass="from-rose-500 to-pink-600"
        iconColorClass="text-rose-500"
        buttonGradientClass="from-rose-500 to-pink-600"
        title="Biens Immobiliers"
        currentTier={user?.subscription_tier}
        features={[
          { icon: Key, title: "Publiez vos biens", description: "Partagez vos opportunités avec la communauté d'investisseurs" },
          { icon: Search, title: "Découvrez des opportunités", description: "Accédez aux biens publiés par les autres membres du Club" },
          { icon: Handshake, title: "Transactions entre membres", description: "Achetez et vendez directement entre investisseurs" }
        ]}
      />
    );
  }

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchProperties(),
        fetchMyProperties(),
        fetchFavorites(),
        fetchSavedListings(),
        fetchAiMatches(),
        fetchSoldProperties()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.ville) params.append('ville', filters.ville);
      if (filters.category) params.append('category', filters.category);
      if (filters.transaction_type) params.append('transaction_type', filters.transaction_type);
      if (filters.price_max) params.append('price_max', filters.price_max);
      
      const response = await axios.get(`${API}/properties?${params.toString()}`, { withCredentials: true });
      setProperties(response.data.properties || []);
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    }
  };

  const fetchMyProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties/my`, { withCredentials: true });
      setMyProperties(response.data || []);
    } catch (error) {
      console.error('Failed to fetch my properties:', error);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/properties/favorites/my`, { withCredentials: true });
      setFavorites(response.data || []);
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    }
  };

  const fetchSoldProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties/sold/list`, { withCredentials: true });
      setSoldProperties(response.data.properties || []);
    } catch (error) {
      console.error('Failed to fetch sold properties:', error);
    }
  };

  const fetchSavedListings = async () => {
    if (!hasFullAccess(user?.subscription_tier)) return;
    try {
      const response = await axios.get(`${API}/ai-agent/saved-listings`, { withCredentials: true });
      setSavedListings(response.data || []);
    } catch (error) {
      console.error('Failed to fetch saved listings:', error);
    }
  };

  const fetchAiMatches = async () => {
    if (user?.subscription_tier !== 'club' && user?.subscription_tier !== 'expert' && user?.subscription_tier !== 'founder') return;
    try {
      const response = await axios.get(`${API}/properties/match/ai`, { withCredentials: true });
      setAiMatches(response.data.properties || []);
    } catch (error) {
      console.error('Failed to fetch AI matches:', error);
    }
  };

  const handleSaveDraft = async () => {
    if (!newProperty.title) {
      toast.error('Veuillez au moins donner un titre à votre brouillon');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...newProperty,
        price: newProperty.price ? parseFloat(newProperty.price) : 0,
        surface: newProperty.surface ? parseFloat(newProperty.surface) : 0,
        nb_pieces: newProperty.nb_pieces ? parseInt(newProperty.nb_pieces) : null,
        nb_chambres: newProperty.nb_chambres ? parseInt(newProperty.nb_chambres) : null,
        loyer_estime: newProperty.loyer_estime ? parseFloat(newProperty.loyer_estime) : null,
        charges_copro: newProperty.charges_copro ? parseFloat(newProperty.charges_copro) : null,
        taxe_fonciere: newProperty.taxe_fonciere ? parseFloat(newProperty.taxe_fonciere) : null,
        travaux_estimes: newProperty.travaux_estimes ? parseFloat(newProperty.travaux_estimes) : null,
        is_draft: true
      };

      if (editingProperty?.property_id) {
        await axios.put(`${API}/properties/${editingProperty.property_id}`, payload, { withCredentials: true });
        toast.success('Brouillon mis à jour !');
      } else {
        await axios.post(`${API}/properties`, payload, { withCredentials: true });
        toast.success('Brouillon sauvegardé ! Retrouvez-le dans "Mes biens".');
      }
      setShowAddDialog(false);
      resetForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublishDraft = async (propertyId) => {
    try {
      await axios.put(`${API}/properties/${propertyId}/publish`, {}, { withCredentials: true });
      toast.success('Bien soumis pour validation !');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la publication');
    }
  };

  const handleEditDraft = (property) => {
    setEditingProperty(property);
    setNewProperty({
      title: property.title || '',
      description: property.description || '',
      category: property.category || '',
      subcategory: property.subcategory || '',
      property_type: property.property_type || 'appartement',
      transaction_type: property.transaction_type || 'vente',
      price: property.price || '',
      surface: property.surface || '',
      nb_pieces: property.nb_pieces || '',
      nb_chambres: property.nb_chambres || '',
      code_postal: property.code_postal || '',
      ville: property.ville || '',
      adresse: property.adresse || '',
      dpe: property.dpe || '',
      loyer_estime: property.loyer_estime || '',
      charges_copro: property.charges_copro || '',
      taxe_fonciere: property.taxe_fonciere || '',
      travaux_estimes: property.travaux_estimes || '',
      parking: property.parking || false,
      balcon: property.balcon || false,
      terrasse: property.terrasse || false,
      jardin: property.jardin || false,
      cave: property.cave || false,
      ascenseur: property.ascenseur || false,
      seller_type: property.seller_type || 'proprietaire',
      is_public: property.is_public !== false
    });
    setShowAddDialog(true);
  };

  const handleCreateProperty = async () => {
    if (!newProperty.title || !newProperty.price || !newProperty.surface || !newProperty.ville || !newProperty.code_postal) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (!newProperty.category || !newProperty.subcategory) {
      toast.error('Veuillez sélectionner une catégorie et une sous-catégorie');
      return;
    }
    if (!authorizationDoc && !editingProperty) {
      toast.error("Le justificatif d'autorisation de commercialisation est obligatoire");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...newProperty,
        price: parseFloat(newProperty.price),
        surface: parseFloat(newProperty.surface),
        nb_pieces: newProperty.nb_pieces ? parseInt(newProperty.nb_pieces) : null,
        nb_chambres: newProperty.nb_chambres ? parseInt(newProperty.nb_chambres) : null,
        loyer_estime: newProperty.loyer_estime ? parseFloat(newProperty.loyer_estime) : null,
        charges_copro: newProperty.charges_copro ? parseFloat(newProperty.charges_copro) : null,
        taxe_fonciere: newProperty.taxe_fonciere ? parseFloat(newProperty.taxe_fonciere) : null,
        travaux_estimes: newProperty.travaux_estimes ? parseFloat(newProperty.travaux_estimes) : null
      };

      let propertyId;
      if (editingProperty?.property_id) {
        // Update existing property
        await axios.put(`${API}/properties/${editingProperty.property_id}`, payload, { withCredentials: true });
        propertyId = editingProperty.property_id;
        
        // If it was a draft, publish it
        if (editingProperty.validation_status === 'draft') {
          await axios.put(`${API}/properties/${propertyId}/publish`, {}, { withCredentials: true });
        }
      } else {
        // Create new property
        const response = await axios.post(`${API}/properties`, payload, { withCredentials: true });
        propertyId = response.data.property_id;
      }

      // Upload authorization document (only if new file selected)
      if (authorizationDoc) {
        try {
          const docFormData = new FormData();
          docFormData.append('file', authorizationDoc);
          await axios.post(`${API}/properties/${propertyId}/authorization-doc`, docFormData, {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000
          });
        } catch (docError) {
          console.error('Authorization doc upload error:', docError);
          toast.warning("Le justificatif n'a pas pu être uploadé.");
        }
      }

      // Upload images if any NEW files (File objects, not existing URLs)
      const newImageFiles = propertyImages.filter(img => img instanceof File);
      if (newImageFiles.length > 0) {
        toast.info('Upload des photos en cours...');
        try {
          const formData = new FormData();
          newImageFiles.forEach(img => formData.append('files', img));
          await axios.post(`${API}/properties/${propertyId}/images`, formData, {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000
          });
        } catch (imgError) {
          console.error('Image upload error:', imgError);
          toast.warning("Certaines photos n'ont pas pu être uploadées.");
        }
      }

      if (editingProperty?.property_id) {
        toast.success('Bien mis à jour avec succès !');
      } else {
        toast.success(payload.validation_status === 'pending'
          ? 'Bien soumis avec succès ! Il sera visible après validation.'
          : 'Bien publié avec succès !');
      }
      setShowAddDialog(false);
      resetForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la publication');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setNewProperty({
      title: '', description: '', category: '', subcategory: '',
      property_type: 'appartement', transaction_type: 'vente',
      price: '', surface: '', nb_pieces: '', nb_chambres: '',
      code_postal: '', ville: '', adresse: '', dpe: '',
      loyer_estime: '', charges_copro: '', taxe_fonciere: '', travaux_estimes: '',
      parking: false, balcon: false, terrasse: false, jardin: false,
      cave: false, ascenseur: false, seller_type: 'proprietaire', is_public: true
    });
    setPropertyImages([]);
    setAuthorizationDoc(null);
    setEditingProperty(null);
  };

  const handleToggleFavorite = async (propertyId) => {
    try {
      const response = await axios.post(`${API}/properties/${propertyId}/favorite`, {}, { withCredentials: true });
      toast.success(response.data.favorited ? 'Ajouté aux favoris' : 'Retiré des favoris');
      fetchFavorites();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce bien ?')) return;
    try {
      await axios.delete(`${API}/properties/${propertyId}`, { withCredentials: true });
      toast.success('Bien supprimé');
      fetchMyProperties();
      fetchProperties();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleMarkSold = async (propertyId) => {
    try {
      const res = await axios.put(`${API}/properties/${propertyId}/mark-sold`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleEditProperty = (property) => {
    setEditingProperty({
      ...property,
      category: property.category || '',
      subcategory: property.subcategory || '',
      price: property.price?.toString() || '',
      surface: property.surface?.toString() || '',
      nb_pieces: property.nb_pieces?.toString() || '',
      nb_chambres: property.nb_chambres?.toString() || '',
      loyer_estime: property.loyer_estime?.toString() || '',
      charges_copro: property.charges_copro?.toString() || '',
      taxe_fonciere: property.taxe_fonciere?.toString() || '',
      travaux_estimes: property.travaux_estimes?.toString() || '',
      seller_type: property.seller_type || 'proprietaire'
    });
    setNewProperty({
      title: property.title || '',
      description: property.description || '',
      category: property.category || '',
      subcategory: property.subcategory || '',
      property_type: property.property_type || 'appartement',
      transaction_type: property.transaction_type || 'vente',
      price: property.price?.toString() || '',
      surface: property.surface?.toString() || '',
      nb_pieces: property.nb_pieces?.toString() || '',
      nb_chambres: property.nb_chambres?.toString() || '',
      code_postal: property.code_postal || '',
      ville: property.ville || '',
      adresse: property.adresse || '',
      dpe: property.dpe || '',
      loyer_estime: property.loyer_estime?.toString() || '',
      charges_copro: property.charges_copro?.toString() || '',
      taxe_fonciere: property.taxe_fonciere?.toString() || '',
      travaux_estimes: property.travaux_estimes?.toString() || '',
      parking: property.parking || false,
      balcon: property.balcon || false,
      terrasse: property.terrasse || false,
      jardin: property.jardin || false,
      cave: property.cave || false,
      ascenseur: property.ascenseur || false,
      seller_type: property.seller_type || 'proprietaire',
      is_public: property.is_public !== false
    });
    setPropertyImages(property.images || []);
    setShowAddDialog(true);
  };

  const handleUpdateProperty = async () => {
    if (!editingProperty) return;
    if (!editingProperty.title || !editingProperty.price || !editingProperty.surface || !editingProperty.ville || !editingProperty.code_postal) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API}/properties/${editingProperty.property_id}`, {
        title: editingProperty.title,
        description: editingProperty.description,
        category: editingProperty.category,
        subcategory: editingProperty.subcategory,
        property_type: editingProperty.property_type,
        transaction_type: editingProperty.transaction_type,
        price: parseFloat(editingProperty.price),
        surface: parseFloat(editingProperty.surface),
        nb_pieces: editingProperty.nb_pieces ? parseInt(editingProperty.nb_pieces) : null,
        nb_chambres: editingProperty.nb_chambres ? parseInt(editingProperty.nb_chambres) : null,
        code_postal: editingProperty.code_postal,
        ville: editingProperty.ville,
        adresse: editingProperty.adresse,
        dpe: editingProperty.dpe,
        loyer_estime: editingProperty.loyer_estime ? parseFloat(editingProperty.loyer_estime) : null,
        charges_copro: editingProperty.charges_copro ? parseFloat(editingProperty.charges_copro) : null,
        taxe_fonciere: editingProperty.taxe_fonciere ? parseFloat(editingProperty.taxe_fonciere) : null,
        travaux_estimes: editingProperty.travaux_estimes ? parseFloat(editingProperty.travaux_estimes) : null,
        parking: editingProperty.parking,
        balcon: editingProperty.balcon,
        terrasse: editingProperty.terrasse,
        jardin: editingProperty.jardin,
        cave: editingProperty.cave,
        ascenseur: editingProperty.ascenseur,
        seller_type: editingProperty.seller_type,
        is_public: editingProperty.is_public
      }, { withCredentials: true });

      toast.success('Bien mis à jour avec succès !');
      setShowEditDialog(false);
      setEditingProperty(null);
      fetchMyProperties();
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSubmitting(false);
    }
  };

  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          }, 'image/jpeg', quality);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length + propertyImages.length > 10) {
      toast.error('Maximum 10 images par bien');
      return;
    }
    toast.info('Compression des images en cours...');
    const compressedFiles = await Promise.all(files.map(file => compressImage(file)));
    setPropertyImages(prev => [...prev, ...compressedFiles]);
    toast.success(`${compressedFiles.length} image(s) ajoutée(s)`);
  };

  // Property Card Component
  const PropertyCard = ({ property, showActions = false }) => {
    const favoriteIds = favorites.map(f => f.property_id);
    const isFav = favoriteIds.includes(property.property_id);
    const SellerIcon = getSellerTypeIcon(property.seller_type);
    const isSold = property.status === 'sold';
    const canMarkSold = property.user_id === user?.user_id || user?.email === 'rajasalame@gmail.com';

    const openDetail = () => {
      setSelectedProperty(property);
      setCurrentImageIndex(0);
      setShowDetailDialog(true);
    };

    const getValidationBadge = () => {
      if (!showActions) return null;
      switch (property.validation_status) {
        case 'draft':
          return <Badge className="bg-slate-500 text-white"><Clock className="w-3 h-3 mr-1" /> Brouillon</Badge>;
        case 'pending':
          return <Badge className="bg-amber-500 text-white"><Clock className="w-3 h-3 mr-1" /> En attente</Badge>;
        case 'approved':
          return <Badge className="bg-green-600 text-white"><CheckCircle className="w-3 h-3 mr-1" /> Approuvé</Badge>;
        case 'rejected':
          return <Badge className="bg-red-600 text-white"><XCircle className="w-3 h-3 mr-1" /> Rejeté</Badge>;
        default:
          return null;
      }
    };

    return (
      <Card 
        className="group overflow-hidden bg-white border-0 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer rounded-2xl" 
        data-testid={`property-card-${property.property_id}`}
        onClick={openDetail}
      >
        <div className="relative h-56 overflow-hidden">
          {property.images?.length > 0 ? (
            <img 
              src={property.images[0]} 
              alt={property.title} 
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
              <Building2 className="w-16 h-16 text-slate-300" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          
          {/* Top badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {isSold && (
              <Badge className="bg-red-600 text-white shadow-lg text-sm font-bold px-3">
                VENDU
              </Badge>
            )}
            <Badge className={`${property.transaction_type === 'vente' ? 'bg-blue-600' : 'bg-emerald-600'} text-white shadow-lg`}>
              {property.transaction_type === 'vente' ? 'Vente' : 'Location'}
            </Badge>
            {property.category && (
              <Badge className="bg-white/90 text-slate-700 text-xs shadow-lg backdrop-blur-sm">
                {getCategoryLabel(property.category)}
              </Badge>
            )}
          </div>
          
          {/* Validation badge or favorite button */}
          {showActions && getValidationBadge() ? (
            <div className="absolute top-3 right-3">{getValidationBadge()}</div>
          ) : !showActions && (
            <div className="absolute top-3 right-3 flex gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); hapticImpact('Light'); nativeShare({ title: property.title, text: `${property.title} - ${property.price?.toLocaleString('fr-FR')}€ à ${property.ville}`, url: `https://www.10minvestor.com/properties` }); }}
                className="w-10 h-10 rounded-full flex items-center justify-center bg-white/90 text-slate-600 hover:bg-white hover:scale-110 transition-all shadow-lg"
                data-testid={`share-property-${property.property_id}`}
              >
                <ExternalLink className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); hapticImpact('Light'); handleToggleFavorite(property.property_id); }}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all shadow-lg ${isFav ? 'bg-red-500 text-white' : 'bg-white/90 text-slate-600 hover:bg-white hover:scale-110'}`}
              >
                <Heart className={`w-5 h-5 ${isFav ? 'fill-current' : ''}`} />
              </button>
            </div>
          )}
          
          {/* Price overlay */}
          <div className="absolute bottom-3 left-3 right-3">
            <p className="text-2xl font-bold text-white drop-shadow-lg">
              {property.price?.toLocaleString('fr-FR')} €
            </p>
            <div className="flex items-center text-white/90 text-sm mt-1">
              <MapPin className="w-4 h-4 mr-1" />
              {property.ville} ({property.code_postal})
            </div>
          </div>
        </div>
        
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-2 mb-3">
            <h3 className="font-semibold text-lg text-slate-800 line-clamp-1 flex-1">{property.title}</h3>
            {property.rendement_brut && (
              <Badge className="bg-emerald-100 text-emerald-700 border-0 whitespace-nowrap">
                <TrendingUp className="w-3 h-3 mr-1" />
                {property.rendement_brut.toFixed(1)}%
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
            <span className="flex items-center gap-1 bg-slate-100 px-2 py-1 rounded-lg">
              <Maximize className="w-4 h-4" />
              {property.surface} m²
            </span>
            {property.nb_pieces && (
              <span className="flex items-center gap-1 bg-slate-100 px-2 py-1 rounded-lg">
                <Home className="w-4 h-4" />
                {property.nb_pieces} p.
              </span>
            )}
            {property.nb_chambres && (
              <span className="flex items-center gap-1 bg-slate-100 px-2 py-1 rounded-lg">
                <Bed className="w-4 h-4" />
                {property.nb_chambres} ch.
              </span>
            )}
          </div>
          
          {property.price_per_m2 && (
            <p className="text-sm text-slate-500 mb-3">
              {property.price_per_m2.toLocaleString('fr-FR')} €/m²
            </p>
          )}

          {/* Seller type badge */}
          <div className="flex items-center gap-2 mb-3">
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
              property.seller_type === 'agent_immobilier' ? 'bg-purple-100 text-purple-700' :
              property.seller_type === 'marchand_biens' ? 'bg-orange-100 text-orange-700' :
              'bg-blue-100 text-blue-700'
            }`}>
              <SellerIcon className="w-3.5 h-3.5" />
              {getSellerTypeLabel(property.seller_type)}
            </div>
          </div>

          {showActions && property.validation_status === 'rejected' && property.rejection_reason && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs text-red-600 flex items-start gap-1">
                <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                {property.rejection_reason}
              </p>
            </div>
          )}

          {showActions && property.validation_status === 'draft' && (
            <div className="mb-3 flex gap-2">
              <Button size="sm" className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-xl" onClick={(e) => { e.stopPropagation(); handleEditDraft(property); }} data-testid={`edit-draft-${property.property_id}`}>
                <Pencil className="w-3 h-3 mr-1" /> Modifier
              </Button>
              <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-xl" onClick={(e) => { e.stopPropagation(); handlePublishDraft(property.property_id); }} data-testid={`publish-draft-${property.property_id}`}>
                Publier
              </Button>
            </div>
          )}
          
          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
            <div className="flex items-center text-sm text-slate-500">
              <Eye className="w-4 h-4 mr-1" />
              {property.views || 0} vues
            </div>
            {showActions ? (
              <div className="flex gap-2">
                {canMarkSold && property.validation_status === 'approved' && (
                  <Button size="sm" variant="outline" className={`rounded-xl ${isSold ? 'text-green-600 hover:text-green-700 hover:bg-green-50' : 'text-amber-600 hover:text-amber-700 hover:bg-amber-50'}`} onClick={(e) => { e.stopPropagation(); handleMarkSold(property.property_id); }} data-testid={`mark-sold-${property.property_id}`}>
                    {isSold ? <RotateCcw className="w-4 h-4" /> : <Tag className="w-4 h-4" />}
                  </Button>
                )}
                <Button size="sm" variant="outline" className="rounded-xl" onClick={(e) => { e.stopPropagation(); handleEditProperty(property); }} data-testid={`edit-property-${property.property_id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" className="rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); handleDeleteProperty(property.property_id); }} data-testid={`delete-property-${property.property_id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="outline" className="rounded-xl" onClick={(e) => { e.stopPropagation(); openDetail(); }}>
                Voir le bien
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Image Gallery with Navigation
  const ImageGallery = ({ images, currentIndex, setCurrentIndex }) => {
    if (!images || images.length === 0) {
      return (
        <div className="relative aspect-video bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center">
          <Building2 className="w-24 h-24 text-slate-300" />
        </div>
      );
    }

    const goToPrevious = () => {
      setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const goToNext = () => {
      setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    };

    return (
      <div className="space-y-4">
        {/* Main Image */}
        <div className="relative aspect-video rounded-2xl overflow-hidden bg-slate-100 group">
          <img 
            src={images[currentIndex]} 
            alt={`Image ${currentIndex + 1}`}
            className="w-full h-full object-cover"
          />
          
          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all opacity-0 group-hover:opacity-100"
                data-testid="gallery-prev-btn"
              >
                <ChevronLeft className="w-6 h-6 text-slate-700" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all opacity-0 group-hover:opacity-100"
                data-testid="gallery-next-btn"
              >
                <ChevronRight className="w-6 h-6 text-slate-700" />
              </button>
            </>
          )}
          
          {/* Image Counter */}
          <div className="absolute bottom-3 right-3 bg-black/60 text-white text-sm px-3 py-1 rounded-full">
            {currentIndex + 1} / {images.length}
          </div>
        </div>
        
        {/* Thumbnails */}
        {images.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {images.map((img, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
                  i === currentIndex ? 'border-blue-600 ring-2 ring-blue-600/30' : 'border-transparent opacity-70 hover:opacity-100'
                }`}
                data-testid={`thumbnail-${i}`}
              >
                <img src={img} alt={`Thumbnail ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const totalProperties = properties.length;
  const totalFavorites = favorites.length + savedListings.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-rose-50/30">
      {/* Header */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-slate-600 hover:text-rose-600 px-2 sm:px-3">
                  <ChevronLeft className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Tableau de bord</span>
                </Button>
              </Link>
              <Link to="/dashboard" className="hidden md:flex">
                <img src="/logo-horizontal.png" alt="10 Millions Investor" className="h-16 sm:h-20 w-auto" />
              </Link>
            </div>
            <Button 
              onClick={() => setShowAddDialog(true)} 
              className="bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 shadow-lg shadow-rose-500/25 text-xs sm:text-sm h-9 sm:h-10"
              data-testid="publish-property-btn"
            >
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Publier un bien</span>
              <span className="sm:hidden ml-1">Publier</span>
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-rose-600 via-pink-600 to-purple-700" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-pink-300 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-12">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0">
                <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-white">Biens Immobiliers</h1>
                <p className="text-rose-100 text-xs sm:text-base">Opportunités partagées par la communauté</p>
              </div>
            </div>
            <div className="flex items-center gap-4 sm:gap-6">
              <div className="text-center">
                <div className="text-xl sm:text-3xl font-bold text-white">{totalProperties}</div>
                <div className="text-rose-200 text-[10px] sm:text-sm">Disponibles</div>
              </div>
              <div className="w-px h-8 sm:h-12 bg-white/20" />
              <div className="text-center">
                <div className="text-xl sm:text-3xl font-bold text-white">{myProperties.length}</div>
                <div className="text-rose-200 text-[10px] sm:text-sm">Mes biens</div>
              </div>
              <div className="hidden sm:flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-xl">
                <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-300" />
                <span className="text-white text-xs sm:text-sm font-medium">Vérifiés</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="overflow-x-auto -mx-1 px-1">
              <TabsList className="bg-white shadow-md rounded-xl p-1 inline-flex min-w-max">
                <TabsTrigger value="all" className="rounded-lg data-[state=active]:bg-rose-600 data-[state=active]:text-white text-xs sm:text-sm">
                  Tous
                </TabsTrigger>
                <TabsTrigger value="my" className="rounded-lg data-[state=active]:bg-rose-600 data-[state=active]:text-white text-xs sm:text-sm">
                  Mes biens ({myProperties.length})
                </TabsTrigger>
                <TabsTrigger value="favorites" className="rounded-lg data-[state=active]:bg-rose-600 data-[state=active]:text-white text-xs sm:text-sm">
                  Favoris ({totalFavorites})
                </TabsTrigger>
                <TabsTrigger value="sold" className="rounded-lg data-[state=active]:bg-rose-600 data-[state=active]:text-white text-xs sm:text-sm">
                  <Tag className="w-3.5 h-3.5 mr-1" /> Vendus ({soldProperties.length})
                </TabsTrigger>
                {(user?.subscription_tier === 'club' || user?.subscription_tier === 'expert' || user?.subscription_tier === 'founder') && (
                  <TabsTrigger value="ai" className="rounded-lg data-[state=active]:bg-rose-600 data-[state=active]:text-white text-xs sm:text-sm">
                    <Bot className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" /> IA
                  </TabsTrigger>
                )}
              </TabsList>
            </div>
            
            {activeTab === 'all' && (
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <Input
                  placeholder="Ville..."
                  value={filters.ville}
                  onChange={(e) => setFilters({ ...filters, ville: e.target.value })}
                  className="w-24 sm:w-32 rounded-xl border-slate-200 text-sm h-9"
                />
                <Select value={filters.category || "all"} onValueChange={(v) => setFilters({ ...filters, category: v === "all" ? "" : v })}>
                  <SelectTrigger className="w-32 sm:w-44 rounded-xl border-slate-200 text-xs sm:text-sm h-9">
                    <SelectValue placeholder="Catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes</SelectItem>
                    {Object.entries(PROPERTY_CATEGORIES).map(([key, cat]) => (
                      <SelectItem key={key} value={key}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filters.transaction_type || "all"} onValueChange={(v) => setFilters({ ...filters, transaction_type: v === "all" ? "" : v })}>
                  <SelectTrigger className="w-24 sm:w-32 rounded-xl border-slate-200 text-xs sm:text-sm h-9">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="vente">Vente</SelectItem>
                    <SelectItem value="location">Location</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={fetchProperties} className="rounded-xl h-9 w-9 p-0" size="icon">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          {/* All Properties Tab */}
          <TabsContent value="all">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                  <Card key={i} className="animate-pulse rounded-2xl overflow-hidden">
                    <div className="h-56 bg-slate-200" />
                    <CardContent className="p-5 space-y-3">
                      <div className="h-4 bg-slate-200 rounded w-3/4" />
                      <div className="h-6 bg-slate-200 rounded w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : properties.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-10 h-10 text-rose-400" />
                </div>
                <p className="text-slate-600 mb-4">Aucun bien disponible pour le moment</p>
                <Button onClick={() => setShowAddDialog(true)} className="bg-gradient-to-r from-rose-500 to-pink-600">
                  <Plus className="w-4 h-4 mr-2" /> Soyez le premier à publier
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {properties.map(prop => (
                  <PropertyCard key={prop.property_id} property={prop} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Properties Tab */}
          <TabsContent value="my">
            {myProperties.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Home className="w-10 h-10 text-rose-400" />
                </div>
                <p className="text-slate-600 mb-4">Vous n'avez pas encore publié de bien</p>
                <Button onClick={() => setShowAddDialog(true)} className="bg-gradient-to-r from-rose-500 to-pink-600">
                  <Plus className="w-4 h-4 mr-2" /> Publier mon premier bien
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myProperties.map(prop => (
                  <PropertyCard key={prop.property_id} property={prop} showActions />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Favorites Tab */}
          <TabsContent value="favorites">
            {favorites.length === 0 && savedListings.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-10 h-10 text-rose-400" />
                </div>
                <p className="text-slate-600">Aucun favori pour le moment</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {favorites.map(prop => (
                  <PropertyCard key={prop.property_id} property={prop} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* AI Match Tab */}
          {(user?.subscription_tier === 'club' || user?.subscription_tier === 'expert' || user?.subscription_tier === 'founder') && (
            <TabsContent value="ai">
              <div className="mb-4 p-4 bg-purple-50 rounded-xl border border-purple-200">
                <div className="flex items-center gap-2 text-purple-700">
                  <Bot className="w-5 h-5" />
                  <span className="font-medium">Biens correspondant à vos critères de recherche IA</span>
                </div>
              </div>
              {aiMatches.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Bot className="w-10 h-10 text-purple-400" />
                  </div>
                  <p className="text-slate-600 mb-4">Effectuez d'abord une recherche avec l'Agent IA</p>
                  <Link to="/ai-agent">
                    <Button className="bg-gradient-to-r from-purple-500 to-indigo-600">
                      Utiliser l'Agent IA
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {aiMatches.map(prop => (
                    <PropertyCard key={prop.property_id} property={prop} />
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          {/* Sold Properties Tab */}
          <TabsContent value="sold">
            {soldProperties.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Tag className="w-10 h-10 text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucun bien vendu</h3>
                <p className="text-slate-500">Les biens marqués comme vendus apparaîtront ici</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {soldProperties.map(prop => (
                  <PropertyCard key={prop.property_id} property={prop} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Property Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Building2 className="w-5 h-5 text-rose-600" />
              {editingProperty ? 'Modifier le brouillon' : 'Publier un bien immobilier'}
            </DialogTitle>
            <DialogDescription>
              {editingProperty ? 'Complétez les informations puis publiez ou sauvegardez' : 'Partagez votre bien avec la communauté d\'investisseurs'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-6 py-4">
            {/* Seller Type Selection */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Vous êtes *</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                {SELLER_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setNewProperty({ ...newProperty, seller_type: type.value })}
                      className={`p-3 sm:p-4 rounded-xl border-2 text-left transition-all ${
                        newProperty.seller_type === type.value
                          ? 'border-rose-500 bg-rose-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Icon className={`w-5 h-5 sm:w-6 sm:h-6 mb-1.5 sm:mb-2 ${newProperty.seller_type === type.value ? 'text-rose-600' : 'text-slate-400'}`} />
                      <p className={`font-medium text-sm ${newProperty.seller_type === type.value ? 'text-rose-700' : 'text-slate-700'}`}>
                        {type.label}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5 sm:mt-1">{type.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="col-span-2">
                <Label>Titre de l'annonce *</Label>
                <Input
                  placeholder="Ex: Appartement T3 rénové centre-ville"
                  value={newProperty.title}
                  onChange={(e) => setNewProperty({ ...newProperty, title: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              
              <div>
                <Label>Catégorie *</Label>
                <Select value={newProperty.category} onValueChange={(v) => setNewProperty({ ...newProperty, category: v, subcategory: '' })}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Choisir une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PROPERTY_CATEGORIES).map(([key, cat]) => (
                      <SelectItem key={key} value={key}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Sous-catégorie *</Label>
                <Select value={newProperty.subcategory} onValueChange={(v) => setNewProperty({ ...newProperty, subcategory: v })} disabled={!newProperty.category}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder={newProperty.category ? "Choisir" : "Sélectionnez d'abord une catégorie"} />
                  </SelectTrigger>
                  <SelectContent>
                    {newProperty.category && PROPERTY_CATEGORIES[newProperty.category]?.subcategories.map((sub) => (
                      <SelectItem key={sub.value} value={sub.value}>{sub.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Type de transaction</Label>
                <Select value={newProperty.transaction_type} onValueChange={(v) => setNewProperty({ ...newProperty, transaction_type: v })}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vente">Vente</SelectItem>
                    <SelectItem value="location">Location</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Prix (€) *</Label>
                <Input type="number" placeholder="150000" value={newProperty.price} onChange={(e) => setNewProperty({ ...newProperty, price: e.target.value })} className="rounded-xl" />
              </div>
              
              <div>
                <Label>Surface (m²) *</Label>
                <Input type="number" placeholder="65" value={newProperty.surface} onChange={(e) => setNewProperty({ ...newProperty, surface: e.target.value })} className="rounded-xl" />
              </div>
              
              <div>
                <Label>Nombre de pièces</Label>
                <Input type="number" placeholder="3" value={newProperty.nb_pieces} onChange={(e) => setNewProperty({ ...newProperty, nb_pieces: e.target.value })} className="rounded-xl" />
              </div>
              
              <div>
                <Label>Chambres</Label>
                <Input type="number" placeholder="2" value={newProperty.nb_chambres} onChange={(e) => setNewProperty({ ...newProperty, nb_chambres: e.target.value })} className="rounded-xl" />
              </div>
            </div>
            
            {/* Location */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label>Ville *</Label>
                <Input placeholder="Lyon" value={newProperty.ville} onChange={(e) => setNewProperty({ ...newProperty, ville: e.target.value })} className="rounded-xl" />
              </div>
              <div>
                <Label>Code postal *</Label>
                <Input placeholder="69000" value={newProperty.code_postal} onChange={(e) => setNewProperty({ ...newProperty, code_postal: e.target.value })} className="rounded-xl" />
              </div>
              <div className="col-span-2">
                <Label>Adresse (optionnel)</Label>
                <Input placeholder="123 rue de la République" value={newProperty.adresse} onChange={(e) => setNewProperty({ ...newProperty, adresse: e.target.value })} className="rounded-xl" />
              </div>
            </div>
            
            {/* Description */}
            <div>
              <Label>Description</Label>
              <Textarea placeholder="Décrivez votre bien..." value={newProperty.description} onChange={(e) => setNewProperty({ ...newProperty, description: e.target.value })} rows={4} className="rounded-xl" />
            </div>
            
            {/* Investment Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label>Loyer estimé (€/mois)</Label>
                <Input type="number" placeholder="850" value={newProperty.loyer_estime} onChange={(e) => setNewProperty({ ...newProperty, loyer_estime: e.target.value })} className="rounded-xl" />
              </div>
              <div>
                <Label>DPE</Label>
                <Select value={newProperty.dpe} onValueChange={(v) => setNewProperty({ ...newProperty, dpe: v })}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map(v => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Charges copro (€/mois)</Label>
                <Input type="number" placeholder="100" value={newProperty.charges_copro} onChange={(e) => setNewProperty({ ...newProperty, charges_copro: e.target.value })} className="rounded-xl" />
              </div>
              <div>
                <Label>Taxe foncière (€/an)</Label>
                <Input type="number" placeholder="800" value={newProperty.taxe_fonciere} onChange={(e) => setNewProperty({ ...newProperty, taxe_fonciere: e.target.value })} className="rounded-xl" />
              </div>
            </div>
            
            {/* Features */}
            <div>
              <Label className="mb-3 block">Caractéristiques</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                {[
                  { key: 'parking', label: 'Parking', icon: Car },
                  { key: 'balcon', label: 'Balcon', icon: Home },
                  { key: 'terrasse', label: 'Terrasse', icon: Home },
                  { key: 'jardin', label: 'Jardin', icon: Leaf },
                  { key: 'cave', label: 'Cave', icon: Home },
                  { key: 'ascenseur', label: 'Ascenseur', icon: Building2 },
                ].map(({ key, label, icon: Icon }) => (
                  <div key={key} className="flex items-center justify-between p-2.5 sm:p-3 bg-slate-50 rounded-xl">
                    <span className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
                      <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400" />
                      {label}
                    </span>
                    <Switch checked={newProperty[key]} onCheckedChange={(v) => setNewProperty({ ...newProperty, [key]: v })} />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Images */}
            <div>
              <Label>Photos du bien (max 10)</Label>
              <div className="mt-2 border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-rose-300 transition-colors cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-600">Cliquez pour ajouter des photos</p>
                <input ref={fileInputRef} type="file" multiple accept="image/*" onChange={handleImageSelect} className="hidden" />
              </div>
              {propertyImages.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {propertyImages.map((img, i) => (
                    <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border">
                      <img src={typeof img === 'string' ? img : URL.createObjectURL(img)} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => setPropertyImages(prev => prev.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs">×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Authorization Document */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <Label className="flex items-center gap-2 text-amber-800">
                <AlertCircle className="w-4 h-4" />
                Justificatif d'autorisation de commercialisation *
              </Label>
              <p className="text-xs text-amber-700 mt-1 mb-3">
                Document obligatoire prouvant votre droit de commercialiser ce bien
              </p>
              <div className="flex items-center gap-3">
                <Button type="button" variant="outline" onClick={() => authDocInputRef.current?.click()} className="rounded-xl">
                  <Upload className="w-4 h-4 mr-2" />
                  {authorizationDoc ? 'Changer le document' : 'Ajouter le document'}
                </Button>
                {authorizationDoc && (
                  <span className="text-sm text-emerald-600 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    {authorizationDoc.name}
                  </span>
                )}
                <input ref={authDocInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setAuthorizationDoc(e.target.files[0])} className="hidden" />
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => { setShowAddDialog(false); setEditingProperty(null); }} className="rounded-xl">
              Annuler
            </Button>
            {!editingProperty && (
              <Button variant="outline" onClick={handleSaveDraft} disabled={submitting} className="rounded-xl border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="save-draft-btn">
                {submitting ? 'Sauvegarde...' : 'Sauvegarder en brouillon'}
              </Button>
            )}
            {editingProperty?.validation_status === 'draft' && (
              <Button variant="outline" onClick={handleSaveDraft} disabled={submitting} className="rounded-xl border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="save-draft-btn">
                {submitting ? 'Sauvegarde...' : 'Mettre à jour le brouillon'}
              </Button>
            )}
            <Button onClick={handleCreateProperty} disabled={submitting} className="bg-gradient-to-r from-rose-500 to-pink-600 rounded-xl" data-testid="publish-property-btn">
              {submitting ? 'En cours...' : (editingProperty ? 'Mettre à jour' : 'Publier le bien')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Property Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Pencil className="w-5 h-5 text-rose-600" />
              Modifier le bien
            </DialogTitle>
          </DialogHeader>
          
          {editingProperty && (
            <div className="grid gap-6 py-4">
              {/* Seller Type */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Vous êtes *</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                  {SELLER_TYPES.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setEditingProperty({ ...editingProperty, seller_type: type.value })}
                        className={`p-3 sm:p-4 rounded-xl border-2 text-left transition-all ${
                          editingProperty.seller_type === type.value
                            ? 'border-rose-500 bg-rose-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Icon className={`w-5 h-5 sm:w-6 sm:h-6 mb-1.5 sm:mb-2 ${editingProperty.seller_type === type.value ? 'text-rose-600' : 'text-slate-400'}`} />
                        <p className={`font-medium text-sm ${editingProperty.seller_type === type.value ? 'text-rose-700' : 'text-slate-700'}`}>
                          {type.label}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="col-span-1 sm:col-span-2">
                  <Label>Titre de l'annonce *</Label>
                  <Input value={editingProperty.title} onChange={(e) => setEditingProperty({ ...editingProperty, title: e.target.value })} className="rounded-xl" />
                </div>
                <div>
                  <Label>Type de transaction</Label>
                  <Select value={editingProperty.transaction_type} onValueChange={(v) => setEditingProperty({ ...editingProperty, transaction_type: v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vente">Vente</SelectItem>
                      <SelectItem value="location">Location</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Prix (€) *</Label>
                  <Input type="number" value={editingProperty.price} onChange={(e) => setEditingProperty({ ...editingProperty, price: e.target.value })} className="rounded-xl" />
                </div>
                <div>
                  <Label>Surface (m²) *</Label>
                  <Input type="number" value={editingProperty.surface} onChange={(e) => setEditingProperty({ ...editingProperty, surface: e.target.value })} className="rounded-xl" />
                </div>
                <div>
                  <Label>Pièces</Label>
                  <Input type="number" value={editingProperty.nb_pieces} onChange={(e) => setEditingProperty({ ...editingProperty, nb_pieces: e.target.value })} className="rounded-xl" />
                </div>
              </div>

              {/* Location */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label>Ville *</Label>
                  <Input value={editingProperty.ville} onChange={(e) => setEditingProperty({ ...editingProperty, ville: e.target.value })} className="rounded-xl" />
                </div>
                <div>
                  <Label>Code postal *</Label>
                  <Input value={editingProperty.code_postal} onChange={(e) => setEditingProperty({ ...editingProperty, code_postal: e.target.value })} className="rounded-xl" />
                </div>
              </div>

              {/* Description */}
              <div>
                <Label>Description</Label>
                <Textarea value={editingProperty.description || ''} onChange={(e) => setEditingProperty({ ...editingProperty, description: e.target.value })} rows={4} className="rounded-xl" />
              </div>

              {/* Features */}
              <div>
                <Label className="mb-3 block">Caractéristiques</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
                  {[
                    { key: 'parking', label: 'Parking' },
                    { key: 'balcon', label: 'Balcon' },
                    { key: 'terrasse', label: 'Terrasse' },
                    { key: 'jardin', label: 'Jardin' },
                    { key: 'cave', label: 'Cave' },
                    { key: 'ascenseur', label: 'Ascenseur' },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center justify-between p-2.5 sm:p-3 bg-slate-50 rounded-xl">
                      <span className="text-xs sm:text-sm">{label}</span>
                      <Switch checked={editingProperty[key]} onCheckedChange={(v) => setEditingProperty({ ...editingProperty, [key]: v })} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="rounded-xl">Annuler</Button>
            <Button onClick={handleUpdateProperty} disabled={submitting} className="bg-gradient-to-r from-rose-500 to-pink-600 rounded-xl">
              {submitting ? 'Mise à jour...' : 'Mettre à jour'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Property Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto p-0">
          {selectedProperty && (
            <>
              {/* Image Gallery */}
              <div className="p-6 pb-4">
                <ImageGallery 
                  images={selectedProperty.images} 
                  currentIndex={currentImageIndex}
                  setCurrentIndex={setCurrentImageIndex}
                />
              </div>

              {/* Property Info */}
              <div className="px-6 pb-6 space-y-6">
                {/* Title & Badges */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800">{selectedProperty.title}</h2>
                    <div className="flex items-center text-slate-600 mt-2">
                      <MapPin className="w-5 h-5 mr-1" />
                      {selectedProperty.adresse && `${selectedProperty.adresse}, `}
                      {selectedProperty.ville} ({selectedProperty.code_postal})
                    </div>
                  </div>
                  <Badge className={`${selectedProperty.transaction_type === 'vente' ? 'bg-blue-600' : 'bg-emerald-600'} text-white text-base px-4 py-1`}>
                    {selectedProperty.transaction_type === 'vente' ? 'Vente' : 'Location'}
                  </Badge>
                </div>

                {/* Price and Key Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
                    <p className="text-sm text-blue-600 font-medium">Prix</p>
                    <p className="text-2xl font-bold text-blue-700">{selectedProperty.price?.toLocaleString('fr-FR')} €</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-xl">
                    <p className="text-sm text-slate-500">Surface</p>
                    <p className="text-xl font-semibold">{selectedProperty.surface} m²</p>
                  </div>
                  {selectedProperty.price_per_m2 && (
                    <div className="bg-slate-50 p-4 rounded-xl">
                      <p className="text-sm text-slate-500">Prix / m²</p>
                      <p className="text-xl font-semibold">{selectedProperty.price_per_m2?.toLocaleString('fr-FR')} €</p>
                    </div>
                  )}
                  {selectedProperty.rendement_brut && (
                    <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-4 rounded-xl">
                      <p className="text-sm text-emerald-600 font-medium">Rendement brut</p>
                      <p className="text-xl font-bold text-emerald-700">{selectedProperty.rendement_brut?.toFixed(1)}%</p>
                    </div>
                  )}
                </div>

                {/* Property Details */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {selectedProperty.category && (
                    <div className="flex items-center gap-2 p-3 bg-white border rounded-xl">
                      <Building2 className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Catégorie</p>
                        <p className="font-medium text-sm">{getCategoryLabel(selectedProperty.category)}</p>
                      </div>
                    </div>
                  )}
                  {selectedProperty.nb_pieces && (
                    <div className="flex items-center gap-2 p-3 bg-white border rounded-xl">
                      <Home className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Pièces</p>
                        <p className="font-medium">{selectedProperty.nb_pieces}</p>
                      </div>
                    </div>
                  )}
                  {selectedProperty.nb_chambres && (
                    <div className="flex items-center gap-2 p-3 bg-white border rounded-xl">
                      <Bed className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Chambres</p>
                        <p className="font-medium">{selectedProperty.nb_chambres}</p>
                      </div>
                    </div>
                  )}
                  {selectedProperty.dpe && (
                    <div className="flex items-center gap-2 p-3 bg-white border rounded-xl">
                      <Zap className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">DPE</p>
                        <p className="font-medium">{selectedProperty.dpe}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Financial Details */}
                {(selectedProperty.loyer_estime || selectedProperty.charges_copro || selectedProperty.taxe_fonciere) && (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <h4 className="font-semibold text-emerald-700 flex items-center gap-2 mb-3">
                      <TrendingUp className="w-5 h-5" />
                      Données financières
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {selectedProperty.loyer_estime && (
                        <div>
                          <p className="text-sm text-slate-600">Loyer estimé</p>
                          <p className="font-semibold">{selectedProperty.loyer_estime?.toLocaleString('fr-FR')} €/mois</p>
                        </div>
                      )}
                      {selectedProperty.charges_copro && (
                        <div>
                          <p className="text-sm text-slate-600">Charges copro</p>
                          <p className="font-semibold">{selectedProperty.charges_copro?.toLocaleString('fr-FR')} €/mois</p>
                        </div>
                      )}
                      {selectedProperty.taxe_fonciere && (
                        <div>
                          <p className="text-sm text-slate-600">Taxe foncière</p>
                          <p className="font-semibold">{selectedProperty.taxe_fonciere?.toLocaleString('fr-FR')} €/an</p>
                        </div>
                      )}
                      {selectedProperty.travaux_estimes && (
                        <div>
                          <p className="text-sm text-slate-600">Travaux estimés</p>
                          <p className="font-semibold">{selectedProperty.travaux_estimes?.toLocaleString('fr-FR')} €</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Features */}
                {(selectedProperty.parking || selectedProperty.balcon || selectedProperty.terrasse || selectedProperty.jardin || selectedProperty.cave || selectedProperty.ascenseur) && (
                  <div>
                    <h4 className="font-semibold mb-3">Caractéristiques</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProperty.parking && <Badge variant="outline" className="rounded-lg"><Car className="w-3 h-3 mr-1" /> Parking</Badge>}
                      {selectedProperty.balcon && <Badge variant="outline" className="rounded-lg">Balcon</Badge>}
                      {selectedProperty.terrasse && <Badge variant="outline" className="rounded-lg">Terrasse</Badge>}
                      {selectedProperty.jardin && <Badge variant="outline" className="rounded-lg"><Leaf className="w-3 h-3 mr-1" /> Jardin</Badge>}
                      {selectedProperty.cave && <Badge variant="outline" className="rounded-lg">Cave</Badge>}
                      {selectedProperty.ascenseur && <Badge variant="outline" className="rounded-lg">Ascenseur</Badge>}
                    </div>
                  </div>
                )}

                {/* Description */}
                {selectedProperty.description && (
                  <div>
                    <h4 className="font-semibold mb-2">Description</h4>
                    <p className="text-slate-600 whitespace-pre-wrap">{selectedProperty.description}</p>
                  </div>
                )}

                {/* Owner Info & Actions */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white text-xl font-semibold shadow-lg">
                      {selectedProperty.owner_name?.charAt(0).toUpperCase() || 'M'}
                    </div>
                    <div>
                      <p className="font-semibold text-lg">{selectedProperty.owner_name || 'Membre'}</p>
                      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium mt-1 ${
                        selectedProperty.seller_type === 'agent_immobilier' ? 'bg-purple-100 text-purple-700' :
                        selectedProperty.seller_type === 'marchand_biens' ? 'bg-orange-100 text-orange-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {React.createElement(getSellerTypeIcon(selectedProperty.seller_type), { className: "w-3.5 h-3.5" })}
                        {getSellerTypeLabel(selectedProperty.seller_type)}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3 w-full sm:w-auto">
                    <Button 
                      variant="outline"
                      onClick={() => handleToggleFavorite(selectedProperty.property_id)}
                      className="flex-1 sm:flex-none rounded-xl"
                      data-testid="detail-favorite-btn"
                    >
                      <Heart className={`w-4 h-4 mr-2 ${favorites.some(f => f.property_id === selectedProperty.property_id) ? 'fill-red-500 text-red-500' : ''}`} />
                      Favoris
                    </Button>
                    <Button 
                      onClick={() => {
                        setShowDetailDialog(false);
                        navigate(`/messages?user=${selectedProperty.user_id}&name=${encodeURIComponent(selectedProperty.owner_name || 'Membre')}&property=${encodeURIComponent(selectedProperty.title)}`);
                      }}
                      className="flex-1 sm:flex-none bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-xl shadow-lg"
                      data-testid="detail-contact-btn"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Contacter
                    </Button>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-sm text-slate-500 pt-2 border-t">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" /> {selectedProperty.views || 0} vues
                    </span>
                    {selectedProperty.created_at && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" /> 
                        Publié le {new Date(selectedProperty.created_at).toLocaleDateString('fr-FR')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertiesPage;
